import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster, toast } from 'sonner';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import SalesPage from '@/pages/SalesPage';
import LeadDetailPage from '@/pages/LeadDetailPage';
import ProjectsPage from '@/pages/ProjectsPage';
import ProjectDetailPage from '@/pages/ProjectDetailPage';
import DWRPage from '@/pages/DWRPage';
import EmployeesPage from '@/pages/EmployeesPage';
import ToolsPage from '@/pages/ToolsPage';
import AMCPage from '@/pages/AMCPage';
import AuditLogPage from '@/pages/AuditLogPage';
import IncidentsPage from '@/pages/IncidentsPage';
import POInvoicePage from '@/pages/POInvoicePage';
import HandoverPage from '@/pages/HandoverPage';
import ExpensesPage from '@/pages/ExpensesPage';
import OverheadsPage from '@/pages/OverheadsPage';
import RevenuePage from '@/pages/RevenuePage';
import ReportsPage from '@/pages/ReportsPage';
import SettingsPage from '@/pages/SettingsPage';
import MyTADAPage from '@/pages/MyTADAPage';
import DirectoryPage from '@/pages/DirectoryPage';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-center">
        <div className="animate-spin w-10 h-10 border-3 border-[#1B5486] border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-sm text-slate-500">Loading PaceOps...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function RoleProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user && !allowedRoles.includes(user.role)) {
      toast.error('Access denied. You do not have permission to view this page.');
      navigate('/dashboard', { replace: true });
    }
  }, [loading, user, allowedRoles, navigate]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-center">
        <div className="animate-spin w-10 h-10 border-3 border-[#1B5486] border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-sm text-slate-500">Loading PaceOps...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (!allowedRoles.includes(user.role)) return null;
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

const ALL_ROLES = ['DIRECTOR', 'SALES', 'ACCOUNTANT', 'HR_ADMIN', 'SUPERVISOR', 'VIEWER'];

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

          {/* Sales - Director, Sales only */}
          <Route path="/sales" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'SALES']}><SalesPage /></RoleProtectedRoute>} />
          <Route path="/sales/:id" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'SALES']}><LeadDetailPage /></RoleProtectedRoute>} />

          {/* Projects - Director, Sales, Supervisor, Accountant, HR_Admin, Viewer */}
          <Route path="/projects" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'SALES', 'SUPERVISOR', 'ACCOUNTANT', 'HR_ADMIN', 'VIEWER']}><ProjectsPage /></RoleProtectedRoute>} />
          <Route path="/projects/:id" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'SALES', 'SUPERVISOR', 'ACCOUNTANT', 'HR_ADMIN', 'VIEWER']}><ProjectDetailPage /></RoleProtectedRoute>} />

          {/* DWR - Director, Supervisor */}
          <Route path="/dwr" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'SUPERVISOR', 'HR_ADMIN']}><DWRPage /></RoleProtectedRoute>} />

          {/* My TA/DA - Sales */}
          <Route path="/my-tada" element={<RoleProtectedRoute allowedRoles={['SALES', 'DIRECTOR', 'HR_ADMIN', 'SUPERVISOR']}><MyTADAPage /></RoleProtectedRoute>} />

          {/* Employees - Director, HR_ADMIN, Accountant */}
          <Route path="/employees" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'HR_ADMIN', 'ACCOUNTANT']}><EmployeesPage /></RoleProtectedRoute>} />

          {/* Tools - Director, HR_ADMIN */}
          <Route path="/tools" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'HR_ADMIN', 'SUPERVISOR', 'ACCOUNTANT']}><ToolsPage /></RoleProtectedRoute>} />

          {/* AMC - Director, Supervisor, HR_Admin */}
          <Route path="/amc" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'SUPERVISOR', 'HR_ADMIN']}><AMCPage /></RoleProtectedRoute>} />

          {/* Audit Log - Director, HR_Admin */}
          <Route path="/audit-log" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'HR_ADMIN']}><AuditLogPage /></RoleProtectedRoute>} />

          {/* Incidents - Director, Supervisor, HR_ADMIN */}
          <Route path="/incidents" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'SUPERVISOR', 'HR_ADMIN']}><IncidentsPage /></RoleProtectedRoute>} />

          {/* PO/Invoices - Director, Accountant, HR_Admin */}
          <Route path="/po-invoices" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN']}><POInvoicePage /></RoleProtectedRoute>} />

          {/* Handover - Director, HR_Admin */}
          <Route path="/handover" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'HR_ADMIN']}><HandoverPage /></RoleProtectedRoute>} />

          {/* Financial - Director, Accountant */}
          <Route path="/expenses" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'ACCOUNTANT', 'SUPERVISOR', 'HR_ADMIN']}><ExpensesPage /></RoleProtectedRoute>} />
          <Route path="/overheads" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN']}><OverheadsPage /></RoleProtectedRoute>} />
          <Route path="/revenue" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN']}><RevenuePage /></RoleProtectedRoute>} />

          {/* Reports - Director, Accountant, HR_Admin, Viewer */}
          <Route path="/reports" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN', 'VIEWER']}><ReportsPage /></RoleProtectedRoute>} />

          {/* Directory - ALL roles */}
          <Route path="/directory" element={<ProtectedRoute><DirectoryPage /></ProtectedRoute>} />

          {/* Settings - Director, HR_Admin */}
          <Route path="/settings" element={<RoleProtectedRoute allowedRoles={['DIRECTOR', 'HR_ADMIN']}><SettingsPage /></RoleProtectedRoute>} />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
